import { dataIdFromObject } from '@sketch/graphql-cache'

// Utils
import { useOnEvent } from '@sketch/utils'

// Components
import { handleFetchMore } from '@sketch/components'

// Hooks
import { useSortingSettings } from 'modules/shares/hooks/useSortSettings'
import { useFlag } from '@sketch/modules-common'

// Types
import {
  useGetProjectCollectionsQuery,
  useGetProjectSharesQuery,
} from '@sketch/gql-types'
import { useRestoreProjectBanner } from '../useRestoreProjectBanner/useRestoreProjectBanner'
import { ShareSearchFilter } from 'modules/shares/hooks/useSearchFilters'

interface UseProjectsProps {
  workspaceId: string
  projectId: string
  search?: string
  filters?: ShareSearchFilter[]
}

// Helpers
const sharesEntriesPath = ['project', 'shares', 'entries']
const collectionsEntriesPath = ['project', 'collections', 'entries']

export const useProject = ({
  workspaceId,
  projectId,
  search,
  filters: baseFilters = [],
}: UseProjectsProps) => {
  // TODO: Remove collections FF
  // https://github.com/sketch-hq/Cloud/issues/15999
  const isCollectionsFFEnabled = useFlag('collections')
  const isSearchFilterSortOn = useFlag('search-filter-sort')

  const filters = [...baseFilters]
  const noSearchCriteria = !search && baseFilters.length === 0
  // When the user has no search term or filters applied, we only want to
  // `getProjectShares` to return shares that are outside collections because
  // we will be displaying collections.
  // If there is a search term or filters applied, we want to return all shares
  // as we will be hiding collections and just showing the search results.
  if (isCollectionsFFEnabled && noSearchCriteria) {
    filters.push('NO_COLLECTION')
  }

  const { sort } = useSortingSettings()
  const {
    data: projectSharesData,
    networkStatus: projectSharesNetworkStatus,
    fetchMore: fetchMoreProjectShares,
    error: projectSharesError,
    refetch: refetchProjectShares,
  } = useGetProjectSharesQuery({
    variables: {
      shortId: projectId,
      after: null,
      search: {
        name: search || null,
        isCurrentVersionDownloadable: null,
        filters,
      },
      sortOrder: isSearchFilterSortOn ? sort : undefined,
    },
    shouldInvalidatePrevious: (prev, curr) => prev?.shortId !== curr?.shortId,
  })

  // Don't fetch collections if:
  // - the feature flag is disabled
  // - OR there is a search term
  // - OR there is at least one filter applied
  //   - **NOTE**: we are checking `baseFilters` here since the `NO_COLLECTION`
  //   filter is automatically added if the `collections` FF is enabled
  const skipCollections =
    !isCollectionsFFEnabled || !!search || baseFilters.length > 0

  const {
    data: collectionsData,
    networkStatus: collectionNetworkStatus,
    fetchMore: fetchMoreCollections,
    error: collectionsError,
  } = useGetProjectCollectionsQuery({
    variables: {
      projectIdentifier: projectId,
      after: null,
    },
    shouldInvalidatePrevious: (prev, curr) =>
      prev?.projectIdentifier !== curr?.projectIdentifier,
    skip: skipCollections,
  })

  useOnEvent(
    'workspaceShareRefresh',
    ({ workspaceIdentifier, projectShortId }) => {
      if (workspaceIdentifier !== workspaceId || projectShortId !== projectId) {
        // Not the workspace visible ignore then
        return
      }
      refetchProjectShares()
    }
  )

  useRestoreProjectBanner({ project: projectSharesData?.project, workspaceId })

  const loading = projectSharesNetworkStatus < 3 || collectionNetworkStatus < 3
  const error = projectSharesError || collectionsError

  const project = projectSharesData?.project
  const afterShares = project?.shares.meta.after ?? null
  const afterCollections =
    collectionsData?.project.collections.meta.after ?? null

  const loadMoreSharesHandler = handleFetchMore(
    fetchMoreProjectShares,
    sharesEntriesPath,
    { dataIdFromObject, after: afterShares }
  )

  const loadMoreCollectionsHandler = handleFetchMore(
    fetchMoreCollections,
    collectionsEntriesPath,
    { dataIdFromObject, after: afterCollections }
  )

  return {
    data: {
      project: projectSharesData?.project,
      collections: collectionsData?.project.collections,
    },
    loadMore: {
      project: loadMoreSharesHandler,
      collections: loadMoreCollectionsHandler,
    },
    loading,
    error,
  }
}
